import React, { useState } from "react";
import { Eye, EyeOff, Lock, User } from "lucide-react";
import API from "../services/api";

function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const canLogin = username.trim().length > 0 && password.trim().length > 0;

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!canLogin) { setMessage("Enter username and password."); return; }

    try {
      setLoading(true);
      const res = await API.post("admin-login", {
        email: username.trim(),
        password,
      });

      if (res.data && res.data.id && (res.data.role === "admin" || res.data.role === "sub_admin")) {
        localStorage.setItem("admin", JSON.stringify({ ...res.data, loginAt: Date.now() }));
        window.location.href = "/admin-dashboard";
        return;
      }
      setMessage("Invalid admin login.");
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.response?.data?.error || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={page}>
      <div style={bgCircle1} />
      <div style={bgCircle2} />

      <div style={card}>
        <div style={logoArea}>
          <div style={logoIcon}>
            <Lock size={28} color="#fff" strokeWidth={2.2} />
          </div>
          <h1 style={brandName}>
            <span style={brandOrange}>POS</span>
            <span style={brandGreen}> Matka</span>
          </h1>
          <p style={brandSub}>Admin Panel</p>
        </div>

        <form onSubmit={handleLogin} style={form}>
          {/* Username */}
          <div style={fieldWrap}>
            <label style={fieldLabel}>Username / Email</label>
            <div style={inputRow}>
              <span style={inputIcon}><User size={18} color="#f97316" /></span>
              <input
                type="text"
                autoComplete="username"
                placeholder="Enter admin email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={input}
              />
            </div>
          </div>

          {/* Password */}
          <div style={fieldWrap}>
            <label style={fieldLabel}>Password</label>
            <div style={inputRow}>
              <span style={inputIcon}><Lock size={18} color="#f97316" /></span>
              <input
                type={showPass ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={input}
              />
              <button type="button" style={eyeBtn} onClick={() => setShowPass((v) => !v)} tabIndex={-1}>
                {showPass ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
              </button>
            </div>
          </div>

          {message && <div style={errorBox}>{message}</div>}

          <button
            type="submit"
            disabled={!canLogin || loading}
            style={canLogin && !loading ? loginBtn : loginBtnOff}
          >
            {loading ? "Logging in..." : "Login to Admin"}
          </button>
        </form>
      </div>
    </div>
  );
}

const PURPLE = "#7c2d12";
const PURPLE_MID = "#ea580c";

const page = {
  minHeight: "100vh",
  background: "#f3f4f7",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  boxSizing: "border-box",
  fontFamily: "Inter, system-ui, sans-serif",
  position: "relative",
  overflow: "hidden",
};

const bgCircle1 = {
  position: "absolute",
  width: "500px",
  height: "500px",
  borderRadius: "50%",
  background: `radial-gradient(circle, rgba(99,102,241,0.14) 0%, transparent 70%)`,
  top: "-180px",
  right: "-120px",
  pointerEvents: "none",
};
const bgCircle2 = {
  position: "absolute",
  width: "400px",
  height: "400px",
  borderRadius: "50%",
  background: `radial-gradient(circle, rgba(49,46,129,0.10) 0%, transparent 70%)`,
  bottom: "-150px",
  left: "-100px",
  pointerEvents: "none",
};

const card = {
  width: "min(420px, 100%)",
  background: "#fff",
  borderRadius: "24px",
  padding: "32px 28px",
  boxShadow: "0 20px 60px rgba(49,46,129,0.14)",
  position: "relative",
  zIndex: 1,
};

const logoArea = {
  textAlign: "center",
  marginBottom: "28px",
};
const logoIcon = {
  width: "64px",
  height: "64px",
  borderRadius: "18px",
  background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_MID})`,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "14px",
  boxShadow: "0 12px 28px rgba(49,46,129,0.28)",
};
const brandName = {
  margin: "0 0 4px",
  fontSize: "20px",
  fontWeight: 900,
  letterSpacing: "-0.01em",
};
const brandOrange = { color: "#f97316" };
const brandGreen  = { color: "#16a34a" };
const brandSub = {
  margin: 0,
  color: "#6b7280",
  fontSize: "13px",
  fontWeight: 600,
};

const form = { display: "grid", gap: "16px" };

const fieldWrap = { display: "grid", gap: "6px" };
const fieldLabel = {
  fontSize: "12px",
  fontWeight: 800,
  color: "#374151",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};
const inputRow = {
  display: "flex",
  alignItems: "center",
  background: "#f8f9fb",
  border: "1.5px solid #e5e7eb",
  borderRadius: "12px",
  overflow: "hidden",
  height: "52px",
  transition: "border-color 0.2s",
};
const inputIcon = {
  padding: "0 12px",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};
const input = {
  flex: 1,
  height: "100%",
  border: 0,
  outline: 0,
  background: "transparent",
  fontSize: "15px",
  color: "#111827",
  fontWeight: 600,
  minWidth: 0,
};
const eyeBtn = {
  border: 0,
  background: "transparent",
  padding: "0 14px",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  flexShrink: 0,
};

const errorBox = {
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#dc2626",
  borderRadius: "10px",
  padding: "10px 14px",
  fontSize: "13px",
  fontWeight: 700,
  textAlign: "center",
};

const loginBtn = {
  width: "100%",
  height: "52px",
  border: 0,
  borderRadius: "13px",
  background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_MID})`,
  color: "#fff",
  fontSize: "16px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 10px 24px rgba(49,46,129,0.28)",
  marginTop: "4px",
};
const loginBtnOff = {
  ...loginBtn,
  background: "#fed7aa",
  color: "#ffedd5",
  boxShadow: "none",
  cursor: "not-allowed",
};

export default AdminLogin;
